/**
 * A backend code enriched with its human-readable label.
 * Returned by the API for fields that reference a lookup table
 * (plan de paie, grille salariale, fonction, etc.).
 */
export type LabeledCode = { code: string; libelle: string | null };

const str = (v: unknown): string | undefined => {
  const s = v != null ? String(v).trim() : '';
  return s !== '' ? s : undefined;
};

/**
 * Maps a raw backend { code, libelle } object to a LabeledCode.
 * Falls back if the backend returns a plain string instead.
 */
export function mapLabeledCode(raw: unknown): LabeledCode | undefined {
  if (raw === null || raw === undefined || raw === '') return undefined;
  if (typeof raw === 'object' && 'code' in (raw as object)) {
    const obj = raw as Record<string, unknown>;
    const code = str(obj.code);
    if (!code) return undefined;
    const libelle = obj.libelle != null ? String(obj.libelle).trim() || null : null;
    return { code, libelle };
  }
  const code = str(raw);
  return code ? { code, libelle: null } : undefined;
}

/**
 * Returns the display string from a raw backend labeled-code value: libelle if present, code as fallback.
 * Use in list mappers where the field must be a plain string.
 */
export function labeledCodeDisplay(raw: unknown): string | undefined {
  const lc = mapLabeledCode(raw);
  if (!lc) return undefined;
  return lc.libelle?.trim() || lc.code || undefined;
}

/**
 * Finds the display label for a code string by searching a mixed options array.
 * Returns libelle (falling back to code) when a matching LabeledCode is found,
 * or undefined when no match exists or the matched entry is a plain string.
 *
 * Used by list filter inputs to show DICO labels for already-selected values,
 * where only the code string is available (MUI Select stores codes as values).
 */
export function resolveLabeledCodeOption(
  code: string,
  options: ReadonlyArray<string | LabeledCode>,
): string | undefined {
  const match = options.find((o): o is LabeledCode => typeof o !== 'string' && o.code === code);
  return match ? (match.libelle ?? match.code) : undefined;
}

/**
 * Builds a memoizable label resolver for a fixed options array and i18n context.
 * Call once (e.g. in useMemo) and reuse across all rows — avoids rebuilding the
 * fallback chain on every render when the list is large.
 *
 * Fallback order:
 *   LabeledCode object  → libelle ?? code
 *   string code         → options array lookup (resolveLabeledCodeOption)
 *                       → getOptionLabel prop
 *                       → i18n prefix
 *                       → raw code
 */
export function createLabelResolver(
  options: ReadonlyArray<string | LabeledCode>,
  t: (key: string, fallback: string) => string,
  prefix?: string,
  getOptionLabel?: (code: string) => string,
): (opt: string | LabeledCode) => string {
  return (opt) => {
    if (typeof opt !== 'string') return opt.libelle ?? opt.code;
    const fromOptions = resolveLabeledCodeOption(opt, options);
    if (fromOptions !== undefined) return fromOptions;
    if (getOptionLabel) return getOptionLabel(opt);
    if (prefix) return t(`${prefix}.${opt}`, opt);
    return opt;
  };
}
